/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

const LINK_REGEX =
  /((http|https):\/\/)?([\w_-]+(?:(?:\.(?!png|jpeg|jpg|gif|pdf|zip|svg|doc|docx|exe)[a-z+_-]+)+))([\w.,@?^=%&:/~+#-()-;]*[\w@?^=%&/~+#-()-])/g;

const EMAIL_REGEX =
  // eslint-disable-next-line no-control-regex
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/g;

/**
 * This decorator finds all the links and emails in a node and returns them with either the
 * email or the link flag set to true. This can be used with "Editable" to render the links
 */
export const linkAndEmailDecorator = ([node, path]: [any, number[]]): any => {
  // the text of the node
  const nodeText = node.text;

  // if no text, return an empty array
  if (!nodeText) return [];

  const emails = findEmailsInText(nodeText);
  const urls = findUrlsInText(nodeText);

  // find the emails to return
  const retEmails = emails.map(([url, index]: any) => {
    return {
      anchor: {
        path,
        offset: index,
      },
      focus: {
        path,
        offset: index + url.length,
      },
      email: true,
    };
  });

  // find the links to return
  const retUrls = urls.map(([url, index]: any) => {
    // double check that the url is not an email
    let isEmail = false;
    emails.map(([email, emailIndex]: any) => {
      if (emailIndex + email.length > index && emailIndex < index) {
        isEmail = true;
      }
    });
    // if it is an email, don't return it
    if (isEmail) return;
    // if it is not an email, return it
    return {
      anchor: {
        path,
        offset: index,
      },
      focus: {
        path,
        offset: index + url.length,
      },
      link: true,
    };
  });
  // return all the links and emails (with links filtered for undefined values)
  return [...retUrls.filter((url) => url), ...retEmails];
};

// find all the emails in a text
const findEmailsInText = (text: string) => {
  const emailMatches = text.match(EMAIL_REGEX);
  return emailMatches ? emailMatches.map((m) => [m.trim(), text.indexOf(m.trim())]) : [];
};

// find all the urls in a text
const findUrlsInText = (text: string) => {
  const linkMatches = text.match(LINK_REGEX);
  return linkMatches ? linkMatches.map((m) => [m.trim(), text.indexOf(m.trim())]) : [];
};
